import App, { AppProps } from 'next/app'
import qs from 'qs'
import axios from 'axios'
import browser from 'browser-detect'
import { useEffect } from 'react'
import getConfig from 'next/config'
import Head from 'next/head'
import * as Sentry from '@sentry/node'
import { Integrations } from '@sentry/tracing'
import { Settings } from 'luxon'
import { DefaultSeo } from 'next-seo'
import { RewriteFrames } from '@sentry/integrations'
import { ApolloProvider } from '@apollo/client'
import { useState } from 'react'
import { Hydrate, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { initializeApollo, useApollo } from '~/app/apollo'
import {
  getCredentials,
  InitializeKinesisMeta,
  initializeKinesisTracker,
} from '~/app/utils/kinesis'
import LoadingProvider from '~/app/components/common/LoadingProvider'
import ForceRerenderProvider from '~/app/components/common/ForceRerenderProvider'
import RouterLoading from '~/app/components/common/RouterLoading'
import SEO from '../next-seo.config'
import conf from '~/app/conf'

import '~/app/scss/fix.scss'
import '~/app/scss/feature-flags'

// put here to avoid following error:
// Global CSS cannot be imported from files other than your Custom <App>.
import 'swiper/css'
import 'swiper/css/pagination'
import 'swiper/css/navigation'
import createCache from '@emotion/cache'
import { CacheProvider } from '@emotion/react'

Settings.defaultLocale = 'ko-KR'
Settings.defaultZone = 'Asia/Seoul'
try {
  // 구형 크롬(6x 버전)의 경우 Time zone 설정 이슈가 있다.
  // luxon 에서 UTC+9 -> Etc/GMT+9 로 변환하는데 Etc/GMT+9 로 time zone name 을 설정하면 에러가 발생한다
  // 그래서 IANA 가 지원되는지 여부를 먼저 확인하여 IANA 코드를 먼저 사용하되, 없는 경우에만 UTC+9 를 사용한다
  const dtf = new Intl.DateTimeFormat('en-US', { timeZone: 'Asia/Seoul' })
  if (dtf) {
    Settings.defaultZone = 'Asia/Seoul'
  }
} catch (e) {}

import { GlobalContext, GlobalState } from '~/app/components/common/GlobalProvider'
import { getMobileOperatingSystem } from '~/api/util'
import { Integration, TransactionContext } from '@sentry/types'
import { Braze } from '~/app/braze/braze'
import {
  InitializeKinesisMetaDocument,
  InitializeKinesisMetaQuery,
} from '~/__generated__/app/query/initialize-kinesis-meta.graphql'
import useIsomorphicLayoutEffect from '~/app/utils/useIsomorphicLayoutEffect'
import getIsServer from '~/app/utils/getIsServer'
import { getAppDevice, getIsWebviewDevice } from '~/app/utils/yaNativeUtils'
import { featureFlagEnv, FeatureFlagProvider } from '~/app/utils/feature-flag'
import StyledThemeProvider from '~/app/components/common/StyledThemeProvider'
import { featureFlagsConfig } from '~/app/conf/feature-flag-config'

if (process.env.NEXT_PUBLIC_SENTRY_DSN) {
  const integrations = ((): Integration[] => {
    const integrationsRes = [
      new Integrations.BrowserTracing({
        tracingOrigins: [/https:\/\/((dev|qa|stage)-platform-site|platform-site)\.yanolja\.com/],
        beforeNavigate(context: TransactionContext): TransactionContext {
          return {
            ...context,
            name: location.pathname
              .replace(/\/Y[a-zA-Z0-9]{11}/g, '/<y-reservation-id>')
              .replace(/\/share\/[a-zA-z0-9]{10,22}/g, '/share/<uuid>')
              .replace(/\/\d+/g, '/<digits>')
              .replace(/^\//, ''),
          }
        },
      }),
    ] as unknown as Integration[]
    const config = getConfig()
    const rootDir = config.serverRuntimeConfig.rootDir
    if (!rootDir) {
      return integrationsRes
    }
    return [
      ...integrationsRes,
      new RewriteFrames({
        iteratee: frame => {
          frame.filename = frame!.filename!.replace(`${rootDir}/.next`, 'app:///_next')
          frame.filename = frame!.filename!.replace(`${rootDir}/_next`, 'app:///_next')
          return frame
        },
      }),
    ]
  })()

  const samplRates =
    process.env.EKS_ENV === 'true'
      ? { tracesSampleRate: 0, profilesSampleRate: 0 }
      : { tracesSampleRate: 0.01 }

  Sentry.init({
    enabled: process.env.NODE_ENV === 'production',
    environment: conf.environment,
    integrations,
    dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
    ...samplRates,
  })
}
function CustomApp({
  Component,
  pageProps,
  device,
  kinesisMeta,
  err,
}: AppProps & { kinesisMeta: InitializeKinesisMeta } & { device: string; err: Error }) {
  /**
   * emotion의 speedy 옵션 비활성화를 위한 캐시 설정
   * 컴포넌트 밖에서 캐시를 선언할 경우 스타일이 registered 되지 않아 FOUC 발생
   * @link {https://yanolja.atlassian.net/wiki/spaces/~hyojun.park/pages/179142715/Inspectlet}
   */
  const emotionCache = createCache({ key: 'platform-site', speedy: false })
  const apolloClient = useApollo(pageProps)
  const isServer = getIsServer()
  if (!isServer) {
    // Braze 는 장바구니, 주문, 주문 완료 페이지 내에서만 호출됨
    Braze.initialize(!!kinesisMeta.hashedMemberNo)
  }
  const [queryClient] = useState(() => new QueryClient())

  /**
   * Kinesis initialize
   * - client-side useLayoutEffect 를 사용하여 초기화
   */
  useIsomorphicLayoutEffect(() => {
    initializeKinesisTracker(kinesisMeta)
  }, [])

  useEffect(() => {
    /**
     * 모바일 웹에서 100vh 가 화면에 보이는 크기와 달라서 하단의 내용이 잘리는 문제가 있다
     * 화면이 resize 될때마다 실제로 보이는 크기를 구해서 그 크기의 1%가 어느정도인지 측정하여 사용한다
     */
    const onResize = () => {
      // We execute the same script as before
      const vh = window.innerHeight * 0.01
      document.documentElement.style.setProperty('--vh', `${vh}px`)
    }

    window.addEventListener('resize', onResize)
    return () => window.addEventListener('resize', onResize)
  }, [])

  const globalValue: GlobalState = {
    device,
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Hydrate state={pageProps.dehydratedState}>
        <Head>
          <title>NOL | 놀수록 놀라운 세상</title>
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1.0, maximum-scale=1, user-scalable=no shrink-to-fit=no, viewport-fit=cover"
          />
        </Head>
        <DefaultSeo {...SEO} />
        <ForceRerenderProvider>
          <GlobalContext.Provider value={globalValue}>
            <LoadingProvider>
              <ApolloProvider client={apolloClient}>
                <FeatureFlagProvider
                  featureFlagsConfig={featureFlagsConfig}
                  featureFlagEnv={featureFlagEnv}
                >
                  <StyledThemeProvider>
                    <CacheProvider value={emotionCache}>
                      <RouterLoading>
                        <div style={{ position: 'relative', zIndex: 0 }}>
                          <Component {...pageProps} err={err} />{' '}
                        </div>
                      </RouterLoading>
                    </CacheProvider>
                  </StyledThemeProvider>
                </FeatureFlagProvider>
              </ApolloProvider>
            </LoadingProvider>
          </GlobalContext.Provider>
        </ForceRerenderProvider>
      </Hydrate>
    </QueryClientProvider>
  )
}

const ONE_YEAR = 60 * 60 * 24 * 365

const makeCookieString = (key, value, maxAgeYears = 5) => {
  const maxAge = ONE_YEAR * maxAgeYears
  return `${key}=${value}; Max-Age=${maxAge}; Domain=.yanolja.com; Path=/; SameSite=Lax`
}

CustomApp.getInitialProps = async appContext => {
  if (typeof window === 'undefined') {
    // Creates ywt session
    const { req, res } = appContext.ctx
    const {
      ywt = '',
      token = '',
      cookie = '',
      version = '',
      'nol-app-version': nolAppVersion = '', // 헤더에서는 nol-app-version (하이픈)
    } = req.headers
    // native 환경에서는 쿠키가 아니라 헤더에 담아서 보내줌.
    const headerCgntId = req.headers?.cgntid || req.headers?.cgntId || ''
    // 구 국내 주문 페이지에서 7.50.0 미만인 경우에만 아래 정보를 보내준다
    const { appVersion = '', uuid = '', adid = '' } = appContext.ctx.query
    const cookieCgntId = req.cookies?.cgntId ?? ''
    const cookieForHeader: string[] = []
    const cookieForResponse: string[] = []
    const appDevice = getAppDevice(appContext.ctx)

    if (version) {
      cookieForResponse.push(makeCookieString('version', version))
      cookieForHeader.push(qs.stringify({ version }, { encodeValuesOnly: true }))
    }
    if (nolAppVersion) {
      // 쿠키에서는 nol_app_version (언더스코어)로 저장
      cookieForResponse.push(makeCookieString('nol_app_version', nolAppVersion, 1))
      cookieForHeader.push(
        qs.stringify({ nol_app_version: nolAppVersion }, { encodeValuesOnly: true })
      )
    }
    if (getIsWebviewDevice(appDevice)) {
      cookieForResponse.push(makeCookieString('app-device', appDevice))
      cookieForHeader.push(qs.stringify({ appDevice }, { encodeValuesOnly: true }))
      cookieForResponse.push(makeCookieString('device', appDevice))
      cookieForHeader.push(qs.stringify({ device: appDevice }, { encodeValuesOnly: true }))

      if (appDevice === 'android' && adid) {
        cookieForResponse.push(makeCookieString('adid', adid))
        cookieForHeader.push(qs.stringify({ adid }, { encodeValuesOnly: true }))
      }

      if (appDevice === 'ios' && uuid) {
        cookieForResponse.push(makeCookieString('uuid', uuid))
        cookieForHeader.push(qs.stringify({ uuid }, { encodeValuesOnly: true }))
      }
    }

    let cgntId = headerCgntId || cookieCgntId
    if (!cgntId) {
      const newCgntId = await getCredentials()
      cgntId = newCgntId?.identityId
    }
    cookieForResponse.push(makeCookieString('cgntId', cgntId))
    cookieForHeader.push(qs.stringify({ cgntId }, { encodeValuesOnly: true }))
    appContext.ctx.req.headers.cookie = [cookie, cookieForHeader.join('; ')].join('; ')
    const r = await axios({
      method: 'post',
      url: 'http://localhost:3000/api/auth/ywt',
      headers: {
        ...req.headers,
        ywt,
        token,
        cookie, // 이미 클라이언트에서 넘어온 쿠키가 있다면 새로운 불필요하게 yanolja_sid 를 생성하지 않도록 연결
        cgntid: cgntId,
        device: appDevice, // wv 열때 전달되는 device
        version, // wv 열때 전달되는 device
        adid, // 구버전에서 전달되는 adid
        uuid, // 구버전에서 전달되는 uuid
        ['app-version']: appVersion, // 구버전에서 전달되는 appVersion
        ['app-device']: appDevice, // wv 열때 전달되는 device
      },
    }).catch(err => console.log(err))
    if (r && r.headers && r.headers['set-cookie']) {
      /**
       * ywt 를 이용해 로그인 처리에 성공 했다면 이후 SSR graphql 에서 사용할 수 있도록 yanolja_sid 를 연결해줌
       * client 까지 응답이 한번 내려간 후라면 set-cookie 에 의해 쿠키가 설정된 상황이므로 이 로직을 수행하지 않음
       */
      const responseCookie = r.headers['set-cookie']
      if (typeof responseCookie === 'string') {
        cookieForResponse.push(responseCookie)
      } else if (Array.isArray(responseCookie)) {
        responseCookie.forEach(c => cookieForResponse.push(c))
      }
      const newCookies = responseCookie.map(cookie => {
        const [keyValue] = cookie.split(';')
        return keyValue.trim()
      })
      appContext.ctx.req.headers.cookie = [
        appContext.ctx.req.headers.cookie,
        newCookies.join('; '),
      ].join('; ')
    }
    if (cookieForResponse.length > 0) {
      res.setHeader('Set-Cookie', cookieForResponse)
    }
  }

  const userAgent = appContext.ctx.req
    ? appContext.ctx.req.headers['user-agent']
    : navigator.userAgent
  const appProps = await App.getInitialProps(appContext)

  const apolloClient = initializeApollo(null, appContext.ctx)
  const { data: kinesisMetaData } = await apolloClient
    .query<InitializeKinesisMetaQuery>({
      query: InitializeKinesisMetaDocument,
    })
    .catch(() => ({
      data: {
        memberClassCode: '',
        viewer: {
          hashedMemberNo: '',
          nolMemberProxyId: '',
        },
      },
    }))

  const kinesisMeta = {
    /**
     * 타입상 memberClass 가 비어있을 수 없지만 _app 에서 실행되는 로직으로 방어코드를 넣어줌
     */
    memberGrade: kinesisMetaData.memberClassCode,
    hashedMemberNo: kinesisMetaData.viewer.hashedMemberNo,
    nolMemberProxyId: kinesisMetaData.viewer.nolMemberProxyId || '',
  }

  return {
    ...appProps,
    ...browser(userAgent),
    kinesisMeta,
    device: getMobileOperatingSystem(userAgent),
  }
}

export default CustomApp
